import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, AlertCircle, FileText, GitBranch, Lightbulb, BookOpen, 
  Package, Users, BarChart3, Inbox, UserCog, Clock, Store, ShoppingBag, 
  ClipboardList, CheckSquare, Star, MessageCircle, Settings, Menu, X, 
  Plug, FolderTree, Trash2, Key, ScrollText, Shield, ChevronDown, ChevronRight,
  Headphones, Briefcase, Wrench, PieChart, Cog, ChevronsLeft, ChevronsRight, Activity, Megaphone, Workflow, Bell, Sparkles, TrendingUp, Phone, Route, Code2, History
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useTheme } from '../context/ThemeContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { isRouteEnabled, loading: subscriptionLoading } = useSubscription();
  const { theme } = useTheme();
  
  // Collapsed state - persisted in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  
  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    home: true,
    serviceDesk: true,
    serviceRequests: true,
    workManagement: true,
    resources: true,
    analytics: false,
    administration: false,
    system: false
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSection = (section) => {
    if (isCollapsed) return; // Don't toggle sections when collapsed
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const logoSrc = theme === 'dark' ? '/kineserve-logo-white.svg' : '/kineserve-logo.svg';

  // Define navigation structure with sections
  const navSections = [
    {
      id: 'home',
      label: 'Home',
      icon: LayoutDashboard,
      roles: ['end_user', 'itil_staff', 'admin', 'super_admin'],
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
        { path: '/support-chat', label: 'Chat with Amie', icon: MessageCircle, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
        { path: '/request-callback', label: 'Request a Callback', icon: Phone, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
      ]
    },
    {
      id: 'serviceDesk',
      label: 'Service Desk',
      icon: Headphones,
      roles: ['end_user', 'itil_staff', 'admin', 'super_admin'],
      items: [
        { path: '/incidents', label: 'Incidents', icon: AlertCircle, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
        { 
          path: '/changes', 
          label: 'Changes', 
          icon: GitBranch, 
          roles: ['itil_staff', 'admin', 'super_admin'],
          subItems: [
            { path: '/cab-console', label: 'CAB Console', icon: Shield, roles: ['itil_staff', 'admin', 'super_admin'] }
          ]
        },
        { path: '/problems', label: 'Problems', icon: Lightbulb, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/problems/dashboard', label: 'Problem Trends', icon: PieChart, roles: ['itil_staff', 'admin', 'super_admin'] },
      ]
    },
    {
      id: 'serviceRequests',
      label: 'Service Requests',
      icon: ShoppingBag,
      roles: ['end_user', 'itil_staff', 'admin', 'super_admin'],
      items: [
        { path: '/request-catalog', label: 'Request an Item', icon: ShoppingBag, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
        { path: '/my-requests', label: 'My Requests', icon: ClipboardList, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
        { path: '/service-requests-queue', label: 'Request Fulfillment', icon: CheckSquare, roles: ['itil_staff', 'admin', 'super_admin'] },
      ]
    },
    {
      id: 'workManagement',
      label: 'Work Management',
      icon: Briefcase,
      roles: ['itil_staff', 'admin', 'super_admin'],
      items: [
        { path: '/work-console', label: 'Work Console', icon: Inbox, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/supervisor-dashboard', label: 'Supervisor Dashboard', icon: Activity, roles: ['admin', 'super_admin'] },
        { path: '/queue', label: 'Ticket Queue', icon: AlertCircle, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/task-queue', label: 'Task Queue', icon: CheckSquare, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/live-chat', label: 'Live Chat', icon: Headphones, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/chat-history', label: 'Chat History', icon: History, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/call-center', label: 'Call Center', icon: Phone, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/amie-learning', label: 'Amie Learning', icon: Sparkles, roles: ['admin', 'super_admin'] },
      ]
    },
    {
      id: 'resources',
      label: 'Resources',
      icon: BookOpen,
      roles: ['end_user', 'itil_staff', 'admin', 'super_admin'],
      items: [
        { path: '/knowledge-management', label: 'Knowledge Management', icon: BookOpen, roles: ['end_user', 'itil_staff', 'admin', 'super_admin'] },
        { path: '/help-center', label: 'Help Center', icon: BookOpen, roles: ['end_user'] },
        { path: '/assets', label: 'My Assets', icon: Package, roles: ['end_user'] },
        { path: '/assets', label: 'Assets', icon: Package, roles: ['itil_staff', 'admin', 'super_admin'] },
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: PieChart,
      roles: ['itil_staff', 'admin', 'super_admin'],
      items: [
        { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/survey-analytics', label: 'Resolution Confirmations', icon: Star, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/nps-dashboard', label: 'NPS Dashboard', icon: TrendingUp, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/nps-surveys', label: 'Manage NPS Surveys', icon: ClipboardList, roles: ['admin', 'super_admin'] },
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      icon: Cog,
      roles: ['admin', 'super_admin'],
      items: [
        { path: '/service-catalog', label: 'Service Catalog', icon: Store, roles: ['admin', 'super_admin'] },
        { path: '/workflow-templates', label: 'Workflow Templates', icon: Workflow, roles: ['admin', 'super_admin'] },
        { path: '/reminder-management', label: 'Automated Reminders', icon: Bell, roles: ['admin', 'super_admin', 'itil_admin'] },
        { path: '/categories', label: 'Categories', icon: FolderTree, roles: ['admin', 'super_admin'] },
        { path: '/assignment-groups', label: 'Assignment Groups', icon: UserCog, roles: ['itil_staff', 'admin', 'super_admin'] },
        { path: '/users', label: 'Users', icon: Users, roles: ['admin', 'super_admin'] },
        { path: '/integrations', label: 'Integrations', icon: Plug, roles: ['admin', 'super_admin'] },
        { path: '/upin-records', label: 'UPIN Records', icon: Key, roles: ['admin', 'super_admin'] },
        { path: '/announcements', label: 'Announcements', icon: Megaphone, roles: ['admin', 'super_admin', 'itil_staff'] },
        { path: '/auto-routing', label: 'Auto-Routing Rules', icon: Route, roles: ['admin', 'super_admin'] },
        { path: '/widget-settings', label: 'Chat Widget', icon: Code2, roles: ['admin', 'super_admin'] },
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: Settings,
      roles: ['super_admin'],
      items: [
        { path: '/system-health', label: 'System Health', icon: Activity, roles: ['super_admin'] },
        { path: '/deleted-items', label: 'Deleted Items', icon: Trash2, roles: ['super_admin'] },
        { path: '/audit-logs', label: 'Audit Logs', icon: ScrollText, roles: ['super_admin'] },
        { path: '/module-subscriptions', label: 'Module Subscriptions', icon: Settings, roles: ['super_admin'] },
      ]
    }
  ];

  // Filter sections and items based on role and module subscription
  const getFilteredSections = () => {
    return navSections
      .filter(section => section.roles.includes(user?.role))
      .map(section => ({
        ...section,
        items: section.items
          .filter(item => item.roles.includes(user?.role))
          .filter(item => subscriptionLoading ? true : isRouteEnabled(item.path))
          .map(item => ({
            ...item,
            subItems: item.subItems?.filter(sub => 
              sub.roles.includes(user?.role) && 
              (subscriptionLoading ? true : isRouteEnabled(sub.path))
            )
          }))
      }))
      .filter(section => section.items.length > 0);
  };

  const filteredSections = getFilteredSections();

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  const isItemActive = (path) => location.pathname === path;
  
  const isSectionActive = (section) => {
    return section.items.some(item => 
      isItemActive(item.path) || 
      item.subItems?.some(sub => isItemActive(sub.path))
    );
  };

  // Render a single nav item (expanded mode)
  const renderNavItem = (item, isSubItem = false) => {
    const Icon = item.icon;
    const isActive = isItemActive(item.path);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    
    return (
      <div key={item.path}>
        <Link
          to={item.path}
          onClick={handleNavClick}
          data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
            isSubItem ? 'ml-6 text-xs' : ''
          } ${
            isActive
              ? 'bg-primary/10 text-primary font-medium'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          }`}
        >
          <Icon className={`flex-shrink-0 ${isSubItem ? 'h-4 w-4' : 'h-4 w-4'}`} />
          <span className="truncate">{item.label}</span>
        </Link>
        {/* Render sub-items */}
        {hasSubItems && (
          <div className="mt-1">
            {item.subItems.map(subItem => renderNavItem(subItem, true))}
          </div>
        )}
      </div>
    );
  };

  // Render a single nav item (collapsed mode with tooltip)
  const renderCollapsedNavItem = (item, isSubItem = false) => {
    const Icon = item.icon;
    const isActive = isItemActive(item.path);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    
    return (
      <div key={item.path}>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to={item.path}
              onClick={handleNavClick}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <Icon className="h-5 w-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
        {/* Sub-items in collapsed mode */}
        {hasSubItems && item.subItems.map(subItem => renderCollapsedNavItem(subItem, true))}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <>
        {/* Mobile overlay */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
        
        {/* Sidebar */}
        <div 
          className={`
            fixed lg:sticky top-0 left-0 z-50 lg:z-auto
            ${isCollapsed ? 'w-16' : 'w-64'} h-screen bg-card border-r border-border flex flex-col
            transform transition-all duration-300 ease-in-out
            ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          data-testid="sidebar"
        >
          {/* Header */}
          <div className={`p-4 border-b border-border flex-shrink-0 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
            {isCollapsed ? (
              <Link to="/dashboard" className="flex items-center justify-center" data-testid="sidebar-logo-link" onClick={handleNavClick}>
                <img 
                  src={logoSrc}
                  alt="KineServe Logo" 
                  className="h-8 w-8 rounded object-contain"
                  data-testid="sidebar-kineserve-logo"
                />
              </Link>
            ) : (
              <>
                <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo-link" onClick={handleNavClick}>
                  <img 
                    src={logoSrc}
                    alt="KineServe Logo" 
                    className="h-10 w-auto rounded"
                    data-testid="sidebar-kineserve-logo"
                  />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground leading-tight">Service</p>
                    <p className="text-xs font-medium text-muted-foreground leading-tight">Management</p>
                  </div>
                </Link>
                <button 
                  onClick={onClose}
                  className="lg:hidden p-2 rounded-md hover:bg-accent"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          
          {/* Navigation */}
          <nav className={`flex-1 ${isCollapsed ? 'p-2' : 'p-3'} space-y-1 overflow-y-auto`} data-testid="sidebar-nav">
            {isCollapsed ? (
              // Collapsed view - icons only with tooltips
              <div className="space-y-1">
                {filteredSections.map((section) => {
                  const sectionActive = isSectionActive(section);
                  
                  return (
                    <div key={section.id} className="space-y-1">
                      {/* Section divider line */}
                      <div className="border-t border-border/50 my-2 first:hidden" />
                      
                      {/* Section items */}
                      {section.items.map(item => renderCollapsedNavItem(item))}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Expanded view - full sections with labels
              filteredSections.map((section) => {
                const SectionIcon = section.icon;
                const isExpanded = expandedSections[section.id];
                const sectionActive = isSectionActive(section);
                
                return (
                  <div key={section.id} className="mb-1">
                    {/* Section Header */}
                    <button
                      onClick={() => toggleSection(section.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        sectionActive 
                          ? 'text-primary' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`section-${section.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <SectionIcon className="h-4 w-4" />
                        <span>{section.label}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {/* Section Items */}
                    {isExpanded && (
                      <div className="mt-1 space-y-0.5 pl-2">
                        {section.items.map(item => renderNavItem(item))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </nav>

          {/* Collapse Toggle Button */}
          <div className={`p-3 border-t border-border flex-shrink-0 ${isCollapsed ? 'flex justify-center' : ''}`}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleCollapse}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors ${
                    isCollapsed ? 'justify-center w-full' : 'w-full'
                  }`}
                  data-testid="sidebar-collapse-toggle"
                  aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {isCollapsed ? (
                    <ChevronsRight className="h-5 w-5" />
                  ) : (
                    <>
                      <ChevronsLeft className="h-5 w-5" />
                      <span>Collapse</span>
                    </>
                  )}
                </button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right">
                  Expand sidebar
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>
      </>
    </TooltipProvider>
  );
};
